import React, { useEffect, useState } from "react";
import { Download } from "lucide-react";
import { Button } from "./ui/button";
import { useLang } from "../i18n/LanguageContext";

// Renders an "Install app" button when the browser fires beforeinstallprompt.
// The event is stashed, then fired on click. Button hides itself once the
// app is installed or the user dismisses the prompt.
export default function InstallButton() {
  const { t } = useLang();
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    // Detect already-installed state (standalone display mode)
    const standalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (standalone) setInstalled(true);

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferred(null);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (installed || !deferred) return null;

  const handleInstall = async () => {
    try {
      deferred.prompt();
      const choice = await deferred.userChoice;
      if (choice?.outcome === "accepted") {
        setInstalled(true);
      }
    } catch {
      // ignore — some browsers throw if prompt was consumed
    } finally {
      setDeferred(null);
    }
  };

  return (
    <Button
      data-testid="install-app-btn"
      onClick={handleInstall}
      variant="outline"
      size="lg"
      className="rounded-full h-10 sm:h-12 px-3 sm:px-4 border-2 font-semibold bg-white gap-2"
    >
      <Download className="w-4 h-4" />
      <span className="hidden sm:inline">{t("install_app")}</span>
    </Button>
  );
}
